import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save,
  Refresh,
  Language,
  Security,
  Notifications,
  Storage,
  CloudUpload,
  CloudDownload,
  Delete,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { settingsAPI } from '../services/api';

const SettingCard = styled(Card)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transition: 'box-shadow 0.3s ease-in-out',
  },
}));

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [backupDialog, setBackupDialog] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [settings, setSettings] = useState({
    timezone: 'UTC',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    theme: 'light',
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
    security: {
      sessionTimeout: 30,
      passwordPolicy: {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: true,
      },
      twoFactorAuth: false,
    },
  });

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Bangkok',
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'th', name: 'ไทย' },
    { code: 'zh', name: '中文' },
    { code: 'ja', name: '日本語' },
  ];

  const dateFormats = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY-MM-DD',
    'DD-MM-YYYY',
  ];

  const timeFormats = ['12h', '24h'];
  const themes = ['light', 'dark'];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsAPI.get();
      setSettings(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch settings');
      console.error('Settings fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.update(settings);
      setSuccess('Settings saved successfully');
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save settings');
      console.error('Settings save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleBackup = async () => {
    try {
      const result = await settingsAPI.backup();
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = `aoc-eto-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setBackupDialog(false);
      setSuccess('Backup created successfully');
    } catch (err) {
      setError('Failed to create backup');
      console.error('Backup error:', err);
    }
  };

  const handleRestore = async (file) => {
    try {
      await settingsAPI.restore(file);
      setRestoreDialog(false);
      setSuccess('Settings restored successfully');
      await fetchSettings();
    } catch (err) {
      setError('Failed to restore settings');
      console.error('Restore error:', err);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleRestore(file);
    }
  };

  const handleSettingChange = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchSettings}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Language />} label="General" />
        <Tab icon={<Security />} label="Security" />
        <Tab icon={<Notifications />} label="Notifications" />
        <Tab icon={<Storage />} label="Backup & Restore" />
      </Tabs>

      {selectedTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Localization
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={settings.timezone}
                    onChange={(e) => handleSettingChange('timezone', e.target.value)}
                    label="Timezone"
                  >
                    {timezones.map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={settings.language}
                    onChange={(e) => handleSettingChange('language', e.target.value)}
                    label="Language"
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Date Format</InputLabel>
                  <Select
                    value={settings.dateFormat}
                    onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                    label="Date Format"
                  >
                    {dateFormats.map((format) => (
                      <MenuItem key={format} value={format}>
                        {format}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Time Format</InputLabel>
                  <Select
                    value={settings.timeFormat}
                    onChange={(e) => handleSettingChange('timeFormat', e.target.value)}
                    label="Time Format"
                  >
                    {timeFormats.map((format) => (
                      <MenuItem key={format} value={format}>
                        {format}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </SettingCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Appearance
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={settings.theme}
                    onChange={(e) => handleSettingChange('theme', e.target.value)}
                    label="Theme"
                  >
                    {themes.map((theme) => (
                      <MenuItem key={theme} value={theme}>
                        {theme.charAt(0).toUpperCase() + theme.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </SettingCard>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Session Management
                </Typography>
                <TextField
                  fullWidth
                  label="Session Timeout (minutes)"
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => handleSettingChange('security.sessionTimeout', parseInt(e.target.value))}
                  margin="normal"
                  inputProps={{ min: 5, max: 480 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.twoFactorAuth}
                      onChange={(e) => handleSettingChange('security.twoFactorAuth', e.target.checked)}
                    />
                  }
                  label="Enable Two-Factor Authentication"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </SettingCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Password Policy
                </Typography>
                <TextField
                  fullWidth
                  label="Minimum Length"
                  type="number"
                  value={settings.security.passwordPolicy.minLength}
                  onChange={(e) => handleSettingChange('security.passwordPolicy.minLength', parseInt(e.target.value))}
                  margin="normal"
                  inputProps={{ min: 6, max: 32 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.passwordPolicy.requireUppercase}
                      onChange={(e) => handleSettingChange('security.passwordPolicy.requireUppercase', e.target.checked)}
                    />
                  }
                  label="Require Uppercase Letters"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.passwordPolicy.requireLowercase}
                      onChange={(e) => handleSettingChange('security.passwordPolicy.requireLowercase', e.target.checked)}
                    />
                  }
                  label="Require Lowercase Letters"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.passwordPolicy.requireNumbers}
                      onChange={(e) => handleSettingChange('security.passwordPolicy.requireNumbers', e.target.checked)}
                    />
                  }
                  label="Require Numbers"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.passwordPolicy.requireSpecialChars}
                      onChange={(e) => handleSettingChange('security.passwordPolicy.requireSpecialChars', e.target.checked)}
                    />
                  }
                  label="Require Special Characters"
                />
              </CardContent>
            </SettingCard>
          </Grid>
        </Grid>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Preferences
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.email}
                      onChange={(e) => handleSettingChange('notifications.email', e.target.checked)}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.push}
                      onChange={(e) => handleSettingChange('notifications.push', e.target.checked)}
                    />
                  }
                  label="Push Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.sms}
                      onChange={(e) => handleSettingChange('notifications.sms', e.target.checked)}
                    />
                  }
                  label="SMS Notifications"
                />
              </CardContent>
            </SettingCard>
          </Grid>
        </Grid>
      )}

      {selectedTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backup & Restore
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Create backups of your system settings and restore from previous backups.
                </Typography>
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={<CloudDownload />}
                    onClick={() => setBackupDialog(true)}
                  >
                    Create Backup
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => setRestoreDialog(true)}
                  >
                    Restore Backup
                  </Button>
                </Box>
              </CardContent>
            </SettingCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Backups
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="System Backup"
                      secondary="Created 2 days ago"
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end">
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Settings Backup"
                      secondary="Created 1 week ago"
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end">
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </SettingCard>
          </Grid>
        </Grid>
      )}

      {/* Backup Dialog */}
      <Dialog open={backupDialog} onClose={() => setBackupDialog(false)}>
        <DialogTitle>Create Backup</DialogTitle>
        <DialogContent>
          <Typography>
            This will create a backup of all system settings. The backup file will be downloaded automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialog(false)}>Cancel</Button>
          <Button onClick={handleBackup} variant="contained">
            Create Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)}>
        <DialogTitle>Restore Backup</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Select a backup file to restore your system settings.
          </Typography>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="restore-file-input"
          />
          <label htmlFor="restore-file-input">
            <Button variant="outlined" component="span" startIcon={<CloudUpload />}>
              Choose File
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
